import styled from 'styled-components';

import { GridActionsCellItem, GridRenderCellParams, GridValueFormatterParams } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertTitle, Chip, Stack, Button as MUIButton } from '@mui/material';
import { AddOutlined, ContentCopyOutlined, DeleteOutlineOutlined, KeyOutlined } from '@mui/icons-material';
import { State, flowActions } from '@store/index';
import { Button } from '@common/Button';
import { Table } from '@common/Table';
import { LayoutWorkspace } from '@common/layout';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { ResourceFallback } from '@common/ResourceFallback';
import { useSecrets } from '@common/hooks/useSecrets';
import { copyToClipboard } from '@common/index';
import { IntegrationSecret } from '@requests/secrets';

const LayoutContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1;
  height: calc(100vh - 61px);

  & .MuiDataGrid-row {
    background-color: transparent !important;
  }

  & .MuiDataGrid-cell {
    cursor: default !important;
  }
`;

const SecretKey = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  & .MuiButton-endIcon {
    display: none;
    margin-left: auto;
    svg {
      font-size: 2ch;
    }
  }

  &:hover {
    & .MuiButton-endIcon {
      display: flex;
    }
  }
`;

export const Secrets = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { service_id } = useParams();
  const { secrets, loading, fetching, refetch, error } = useSecrets();

  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);
  const isEmptySecrets = Object.keys(secrets).length === 0 && !loading;

  const columns = [
    {
      field: 'identifier',
      headerName: `Secret Key`,
      flex: 175,
      sortable: true,
      renderCell: (params: GridValueFormatterParams<string>) => (
        <SecretKey>
          <MUIButton
            startIcon={<KeyOutlined sx={{ fontSize: '3ch !important', marginRight: '0.5rem' }} />}
            color='inherit'
            variant='text'
            fullWidth
            onClick={() => copyToClipboard(params.value)}
            endIcon={<ContentCopyOutlined />}
            sx={{ display: 'flex', justifyContent: 'flex-start', textTransform: 'none', borderRadius: '6px' }}
          >
            {params.value}
          </MUIButton>
        </SecretKey>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 50,
      type: 'string',
      sortable: true,
      renderCell: (params: GridRenderCellParams<IntegrationSecret>) => {
        const { row } = params;
        return (
          <Chip
            color={row.type === 'bearer' ? 'info' : 'primary'}
            label={row.type === 'bearer' ? 'Bearer' : 'Basic'}
            size='small'
            variant='outlined'
            sx={{ fontSize: '1.3ch', height: '19px !important' }}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 75,
      type: 'date',
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        return new Date(params.value).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
      sortable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 40,
      cellClassName: 'actions',
      getActions: ({ id }: { id: string }) => {
        return [
          <GridActionsCellItem
            key='delete'
            icon={<DeleteOutlineOutlined />}
            label='Delete'
            color='inherit'
            onClick={() => dispatch(flowActions.setDeleteSecret(id))}
          />,
        ];
      },
    },
  ];

  const secretEmpty = (
    <LayoutContainer>
      <ServiceNavigation />
      <ResourceFallback
        resourceName='Secret'
        resourceIcon={<KeyOutlined />}
        resourceInfo='Secrets are used to store authentication tokens for external integrations. You need a secret for your Open AI key to run the AI Completion node.'
        onClickCreate={() => dispatch(flowActions.setCreateSecret(true))}
        onClickGuide={() => navigate(`/documentation/secrets`)}
      />
    </LayoutContainer>
  );

  const secretError = (
    <LayoutContainer>
      <ServiceNavigation />
      <Stack p={2} width='100%'>
        <Alert
          severity='error'
          action={
            <Button customColor='red' onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error Loading Secrets</AlertTitle>
          {error?.response?.data.error || error?.message}
        </Alert>
      </Stack>
    </LayoutContainer>
  );

  const secretTable = (
    <LayoutContainer>
      <ServiceNavigation />
      <div style={{ width: 'calc(100vw - 240px - 61px - 3rem)' }}>
        <Table
          width='calc(100vw - 240px - 61px - 3rem)'
          height='calc(100vh - 61px)'
          data={secrets}
          columns={columns}
          loading={loading || fetching}
          density={'standard'}
          sortModel='created_at'
          getRowId={(row) => row.id}
        />
      </div>
    </LayoutContainer>
  );

  const Body = () => {
    if (error) return secretError;
    if (isEmptySecrets) return secretEmpty;
    return secretTable;
  };

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={<Body />}
      actions={
        !isEmptySecrets && (
          <Stack direction='row' spacing={1.5}>
            <Button
              customColor='black'
              startIcon={<AddOutlined sx={{ fontSize: '2.2ch !important' }} />}
              onClick={() => dispatch(flowActions.setCreateSecret(true))}
            >
              Add Secret
            </Button>
          </Stack>
        )
      }
    />
  );
};
